"use client"

import { Link03 } from "@untitledui/icons"

import { EditableText } from "@/components/ui/editable-text"
import { cn } from "@/lib/utils"
import { formatDuration } from "@/lib/utils/"
import type { LookupStatus } from "@/types/contents"

import { suitFor } from "./conversation-suits"

// Display-only projection of the unfurl the composer shows above the answer
// note — the "slip tucked under the card". The title is the one editable
// value (unfurls are often wrong — truncated, clickbait caps, wrong locale);
// source / type / duration are server-authoritative and read-only.
export interface ComposerPreview {
  status: LookupStatus
  /** Rails class-name string, e.g. "VideoBody" — or null when undetected. */
  contentBodyType: string | null
  source: string | null
  thumbnailUrl: string | null
  durationSeconds: number | null
}

// content_body_type ships as the Rails class name; the suit map is keyed by
// the plain content-type string the cards use.
const SUIT_KEY: Record<string, string> = {
  VideoBody: "video",
  AudioBody: "audio",
  ArticleBody: "article",
  PhotoBody: "photo",
}

interface UnfurlPreviewProps {
  loading: boolean
  failed: boolean
  preview: ComposerPreview | null
  title: string
  onTitleChange: (value: string) => void
  titleError?: string
  reduceMotion: boolean
}

// The paper slip that carries the fetched link. Three faces: a shimmer while
// we fetch (so the wait reads as work), the resolved card with an editable
// title, and a graceful "couldn't fetch — mind titling it?" when the link is
// unreachable. It never blocks submission.
export function UnfurlPreview({
  loading,
  failed,
  preview,
  title,
  onTitleChange,
  titleError,
  reduceMotion,
}: UnfurlPreviewProps) {
  const resolved =
    preview != null && (preview.status === "ok" || preview.status === "partial")
  const errored = failed || (preview != null && !resolved)

  // Any pending fetch shows the shimmer — including a re-fetch over a stale
  // result, so the slip never flashes a previous link's details.
  if (loading) {
    return <PreviewSkeleton reduceMotion={reduceMotion} />
  }

  const suit =
    resolved && preview
      ? suitFor(SUIT_KEY[preview.contentBodyType ?? ""] ?? "")
      : null
  const SuitIcon = suit?.icon
  const duration =
    resolved && preview
      ? formatDuration(preview.durationSeconds ?? undefined)
      : null

  return (
    <div className="border-secondary bg-surface-default flex gap-4 rounded-xl border p-3">
      <div className="bg-surface-grouped text-quaternary flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg">
        {resolved && preview?.thumbnailUrl ? (
          <img
            src={preview.thumbnailUrl}
            alt=""
            className="size-full object-cover"
          />
        ) : SuitIcon ? (
          <SuitIcon className={cn("size-6", suit?.color)} />
        ) : (
          <Link03 className="size-6" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <EditableText
          value={title}
          onChange={onTitleChange}
          presentation="display"
          ariaLabel="Title — edit if we got it wrong"
          placeholder={errored ? "Give this a title" : "Untitled"}
          isInvalid={!!titleError}
          bleed={false}
        />

        {errored ? (
          <p className="text-tertiary text-sm">
            We couldn&apos;t fetch the details — mind giving it a title?
          </p>
        ) : (
          <div className="text-tertiary flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
            {SuitIcon && (
              <SuitIcon className={cn("size-4 shrink-0", suit?.color)} />
            )}
            {preview?.source && <span>{preview.source}</span>}
            {duration && (
              <>
                <span aria-hidden>·</span>
                <span>{duration}</span>
              </>
            )}
          </div>
        )}

        {titleError && (
          <p className="text-error-primary text-sm">{titleError}</p>
        )}
      </div>
    </div>
  )
}

function PreviewSkeleton({ reduceMotion }: { reduceMotion: boolean }) {
  const pulse = reduceMotion ? "" : "animate-pulse"
  return (
    <div
      className="border-secondary bg-surface-default flex gap-4 rounded-xl border p-3"
      aria-hidden
    >
      <div
        className={cn("bg-surface-grouped size-20 shrink-0 rounded-lg", pulse)}
      />
      <div className="flex flex-1 flex-col justify-center gap-2">
        <div className={cn("bg-surface-grouped h-4 w-3/4 rounded", pulse)} />
        <div className={cn("bg-surface-grouped h-3 w-2/5 rounded", pulse)} />
      </div>
    </div>
  )
}
